import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  PDP_TAB_SHIPPING,
  PDP_TAB_RETURNS,
  PDP_TAB_CARE,
} from '../../data/pdpLegalCopy';
import { stripSeoRelFromInternalAnchors } from '../../utils/stripHtmlInternalAnchors';

const TabsWrap = styled.div`
  margin-top: 2.5rem;
  padding-top: 2rem;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  max-width: 100%;
`;

const TabList = styled.div`
  display: flex;
  gap: clamp(1rem, 2vw, 2rem);
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const TabButton = styled.button`
  padding-bottom: 1rem;
  font-size: clamp(0.7rem, 0.12vw + 0.65rem, 0.875rem);
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  transition: color 0.2s;
  border-bottom: 2px solid ${({ $active, theme }) =>
    $active ? theme.colors.foreground : 'transparent'};
  margin-bottom: -1px;
  background: none;
  border-top: none;
  border-left: none;
  border-right: none;
  cursor: pointer;
  color: ${({ $active, theme }) =>
    $active ? theme.colors.foreground : theme.colors.mutedForeground};
  flex-shrink: 0;
  white-space: nowrap;
  font-family: ${({ theme }) => theme.fonts.sans};

  &:hover {
    color: ${({ theme }) => theme.colors.foreground};
  }
`;

const TabPanel = styled.div`
  padding: 1.5rem 0 0;
  font-size: ${({ theme }) => theme.type.body};
  line-height: 1.75;
  color: ${({ theme }) => theme.colors.mutedForeground};

  p {
    margin: 0 0 1rem;
  }
`;

const DescriptionHtml = styled.div`
  p {
    margin: 0 0 1rem;
  }
  img {
    max-width: 100%;
    height: auto;
  }
  b,
  strong {
    font-weight: 600;
    color: ${({ theme }) => theme.colors.foreground};
  }
`;

const SpecLine = styled.p`
  margin: 0 0 1rem;
  font-size: ${({ theme }) => theme.type.bodySm};
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.colors.foreground};
`;

/**
 * PDP tab strip (parity with maison/client ProductDetailTabs).
 * Pass `catalogProduct` for static fields; extend with API document when wired.
 */
export default function ProductDetailTabs({ descriptionText, catalogProduct, apiProduct }) {
  const { t } = useTranslation();
  const [active, setActive] = useState('description');
  const tabs = useMemo(
    () => [
      { id: 'description', label: t('modamai.pdpTabs.description') },
      { id: 'shipping', label: t('modamai.pdpTabs.shipping') },
      { id: 'returns', label: t('modamai.pdpTabs.returns') },
      { id: 'care', label: t('modamai.pdpTabs.care') },
    ],
    [t],
  );
  const specBits = useMemo(() => {
    const src = apiProduct || catalogProduct;
    if (!src && !catalogProduct?.slug) return [];
    const bits = [];
    const sku =
      (apiProduct && apiProduct.sku) || catalogProduct?.slug || catalogProduct?.id;
    if (sku) bits.push(t('modamai.pdpTabs.specSku', { sku }));
    if (apiProduct?.currency && apiProduct.currency !== 'USD') {
      bits.push(t('modamai.pdpTabs.specCurrency', { currency: apiProduct.currency }));
    }
    if (
      apiProduct?.stock != null &&
      apiProduct.stock !== '' &&
      !Number.isNaN(Number(apiProduct.stock))
    ) {
      bits.push(t('modamai.pdpTabs.specStock', { stock: apiProduct.stock }));
    }
    return bits;
  }, [apiProduct, catalogProduct, t]);

  const safeDescriptionHtml = useMemo(() => {
    if (!descriptionText) return '';
    if (!/<[a-z][\s\S]*>/i.test(descriptionText)) return descriptionText;
    return stripSeoRelFromInternalAnchors(descriptionText);
  }, [descriptionText]);

  const renderDescription = () => {
    const hasSpecs = specBits.length > 0;
    if (!descriptionText && !hasSpecs) {
      return <TabPanel>{t('modamai.pdpTabs.noDescription')}</TabPanel>;
    }
    const looksHtml = descriptionText && /<[a-z][\s\S]*>/i.test(descriptionText);
    return (
      <TabPanel as="div">
        {hasSpecs ? <SpecLine>{specBits.join(' · ')}</SpecLine> : null}
        {descriptionText ? (
          looksHtml ? (
            <DescriptionHtml dangerouslySetInnerHTML={{ __html: safeDescriptionHtml }} />
          ) : (
            <DescriptionHtml as="div" style={{ whiteSpace: 'pre-wrap' }}>
              {descriptionText}
            </DescriptionHtml>
          )
        ) : null}
      </TabPanel>
    );
  };

  return (
    <TabsWrap>
      <TabList role="tablist" aria-label={t('modamai.pdpTabs.aria')}>
        {tabs.map((tab) => (
          <TabButton
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={active === tab.id}
            $active={active === tab.id}
            onClick={() => setActive(tab.id)}
          >
            {tab.label}
          </TabButton>
        ))}
      </TabList>
      {active === 'description' && renderDescription()}
      {active === 'shipping' && <TabPanel>{PDP_TAB_SHIPPING}</TabPanel>}
      {active === 'returns' && <TabPanel>{PDP_TAB_RETURNS}</TabPanel>}
      {active === 'care' && <TabPanel>{PDP_TAB_CARE}</TabPanel>}
    </TabsWrap>
  );
}
